import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Group,
  Stack,
  Badge,
  Avatar,
  Grid,
  ThemeIcon,
  Alert,
  Progress,
  Divider,
  CopyButton,
  ActionIcon,
  Tooltip
} from '@mantine/core';
import {
  IconUsers,
  IconPlayerPlay,
  IconCopy,
  IconCheck,
  IconClock,
  IconTarget,
  IconTrophy,
  IconAlertTriangle,
  IconWifi,
  IconWifiOff,
  IconDoorExit,
  IconUserPlus
} from '@tabler/icons-react';
import InvitarUsuarioModal from './InvitarUsuarioModal';
import { useNavigate, useParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { 
  SalaMultijugadorResponseDTO, 
  ParticipanteSalaDTO, 
  EstadoSala, 
  EstadoParticipante 
} from '../../types/multijugador';
import { MultijugadorService } from '../../services/multijugadorService';
import { WebSocketService } from '../../services/webSocketService';

interface SalaEsperaProps {
  sala: SalaMultijugadorResponseDTO;
  onSalaActualizada: (sala: SalaMultijugadorResponseDTO) => void;
  onInicioExamen: () => void;
}

const SalaEspera: React.FC<SalaEsperaProps> = ({
  sala,
  onSalaActualizada,
  onInicioExamen
}) => {
  console.log('⏰ [SALA-ESPERA] ====== COMPONENTE MONTADO ======');
  console.log('📊 [SALA-ESPERA] Props recibidas:', {
    sala: {
      codigoSala: sala?.codigoSala,
      nombreSala: sala?.nombreSala,
      estado: sala?.estado,
      cantidadParticipantes: sala?.cantidadParticipantes,
      esCreador: sala?.esCreador,
      nombreCreador: sala?.nombreCreador
    },
    onSalaActualizada: !!onSalaActualizada,
    onInicioExamen: !!onInicioExamen
  });

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [invitarModalOpened, setInvitarModalOpened] = useState(false);
  const webSocketService = WebSocketService.getInstance();

  useEffect(() => {
    console.log('⏰ [SALA-ESPERA] useEffect ejecutándose');
    console.log('   - codigoSala:', sala.codigoSala);
    conectarWebSocket();
    return () => {
      console.log('🧹 [SALA-ESPERA] Cleanup - desuscribiéndose de sala:', sala.codigoSala);
      webSocketService.desuscribirseDeASala(sala.codigoSala);
    };
  }, [sala.codigoSala]);

  const conectarWebSocket = async () => {
    console.log('🌐 [SALA-ESPERA] ====== CONECTANDO WEBSOCKET ======');
    console.log('   - codigoSala:', sala.codigoSala);
    try {
      console.log('🔌 [SALA-ESPERA] Estableciendo conexión WebSocket...');
      await webSocketService.connect();
      console.log('✅ [SALA-ESPERA] WebSocket conectado exitosamente');
      setWsConnected(true);

      console.log('📡 [SALA-ESPERA] Suscribiéndose a eventos de la sala...');
      // Suscribirse a eventos de la sala
      webSocketService.suscribirseASala(sala.codigoSala, {
        onParticipanteUnido: (participante: ParticipanteSalaDTO) => {
          console.log('🚀 [SALA-ESPERA] EVENT: Participante unido:', participante);
          notifications.show({
            title: '¡Nuevo participante!',
            message: `${participante.nombreUsuario} se unió a la sala`,
            color: 'green',
          });
          actualizarSala();
        },
        onParticipanteDesconectado: (participante: ParticipanteSalaDTO) => {
          console.log('🔌 [SALA-ESPERA] EVENT: Participante desconectado:', participante);
          notifications.show({
            title: 'Participante desconectado',
            message: `${participante.nombreUsuario} se desconectó`,
            color: 'orange',
          });
          actualizarSala();
        },
        onInicioExamen: () => {
          console.log('🏁 [SALA-ESPERA] ====== EVENT: INICIO DE EXAMEN ======');
          notifications.show({
            title: '¡El examen ha iniciado!',
            message: 'Redirigiendo al examen...',
            color: 'blue',
          });
          console.log('🔄 [SALA-ESPERA] Ejecutando onInicioExamen callback...');
          onInicioExamen();
          console.log('✅ [SALA-ESPERA] Callback onInicioExamen ejecutado');
        },
        onSalaCancelada: () => {
          console.log('❌ [SALA-ESPERA] EVENT: Sala cancelada');
          notifications.show({
            title: 'Sala cancelada',
            message: 'El creador canceló la sala',
            color: 'red',
          });
          console.log('🔄 [SALA-ESPERA] Navegando a /multijugador...');
          navigate('/multijugador');
        }
      });
      console.log('✅ [SALA-ESPERA] Suscripción a eventos completada');

    } catch (error) {
      console.error('❌ [SALA-ESPERA] Error al conectar WebSocket:', error);
      console.error('   - Error details:', error);
      setWsConnected(false);
    }
  };

  const actualizarSala = async () => {
    console.log('🔄 [SALA-ESPERA] Actualizando datos de la sala...');
    console.log('   - codigoSala:', sala.codigoSala);
    try {
      const salaActualizada = await MultijugadorService.obtenerSala(sala.codigoSala);
      console.log('✅ [SALA-ESPERA] Sala actualizada:', salaActualizada);
      console.log('   - Nuevo estado:', salaActualizada.estado);
      console.log('   - Participantes:', salaActualizada.cantidadParticipantes);
      onSalaActualizada(salaActualizada);
    } catch (error) {
      console.error('❌ [SALA-ESPERA] Error al actualizar sala:', error);
    }
  };

  const handleIniciarExamen = async () => {
    console.log('🏁 [SALA-ESPERA] ====== INICIANDO EXAMEN ======');
    if (!sala.esCreador) {
      console.warn('⚠️ [SALA-ESPERA] Usuario no es creador, no puede iniciar examen');
      return;
    }

    console.log('📊 [SALA-ESPERA] Datos de la sala para iniciar:', {
      codigoSala: sala.codigoSala,
      cantidadParticipantes: sala.cantidadParticipantes,
      estado: sala.estado
    });

    setLoading(true);
    try {
      console.log('🌐 [SALA-ESPERA] Llamando al servicio iniciarExamen...');
      await MultijugadorService.iniciarExamen(sala.codigoSala);
      console.log('✅ [SALA-ESPERA] Examen iniciado exitosamente');
      console.log('💭 [SALA-ESPERA] El WebSocket manejará la navegación...');
      // El WebSocket manejará la navegación
    } catch (error: any) {
      console.error('❌ [SALA-ESPERA] Error al iniciar examen:', error);
      console.error('   - Mensaje:', error.message);
      console.error('   - Response:', error.response?.data);
      notifications.show({
        title: 'Error al iniciar examen',
        message: error.message,
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAbandonarSala = async () => {
    console.log('🚒 [SALA-ESPERA] ====== ABANDONANDO SALA ======');
    console.log('   - codigoSala:', sala.codigoSala);
    try {
      console.log('🌐 [SALA-ESPERA] Llamando al servicio abandonarSala...');
      await MultijugadorService.abandonarSala(sala.codigoSala);
      console.log('✅ [SALA-ESPERA] Sala abandonada exitosamente');
      console.log('🔄 [SALA-ESPERA] Navegando a /multijugador...');
      navigate('/multijugador');
    } catch (error: any) {
      console.error('❌ [SALA-ESPERA] Error al abandonar sala:', error);
      console.error('   - Mensaje:', error.message);
      console.error('   - Response:', error.response?.data);
      notifications.show({
        title: 'Error al abandonar sala',
        message: error.message,
        color: 'red',
      });
    }
  };

  const getEstadoBadgeColor = (estado: EstadoParticipante) => {
    switch (estado) {
      case EstadoParticipante.ESPERANDO: return 'blue';
      case EstadoParticipante.LISTO: return 'green';
      case EstadoParticipante.DESCONECTADO: return 'red';
      default: return 'gray';
    }
  };

  const getEstadoTexto = (estado: EstadoParticipante) => {
    switch (estado) {
      case EstadoParticipante.ESPERANDO: return 'Esperando';
      case EstadoParticipante.LISTO: return 'Listo';
      case EstadoParticipante.DESCONECTADO: return 'Desconectado';
      default: return 'Desconocido';
    }
  };

  const puedeIniciar = sala.esCreador && 
                      sala.estado === EstadoSala.ESPERANDO && 
                      sala.cantidadParticipantes >= 2;

  console.log('🎨 [SALA-ESPERA] ====== RENDERIZANDO ======');
  console.log('📊 [SALA-ESPERA] Estado del render:', {
    wsConnected,
    loading,
    puedeIniciar,
    esCreador: sala.esCreador,
    estado: sala.estado,
    cantidadParticipantes: sala.cantidadParticipantes
  });

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Header de la sala */}
        <div className="text-center">
          <Group justify="center" gap="sm" mb="md">
            <ThemeIcon size={40} variant="light" color="blue">
              <IconUsers size={24} />
            </ThemeIcon>
            <Title order={1} className="text-3xl font-bold text-blue-600">
              {sala.nombreSala}
            </Title>
          </Group>
          
          {/* Código de sala - Destacado para compartir */}
          <Card withBorder padding="lg" className="bg-gradient-to-r from-blue-50 to-indigo-50 mb-md">
            <Stack align="center" gap="xs">
              <Text size="sm" fw={500} color="dimmed">Comparte este código con tus amigos</Text>
              <Group gap="sm">
                <Text
                  size="2rem"
                  fw={700}
                  className="font-mono tracking-widest text-blue-600"
                  style={{ letterSpacing: '0.3em' }}
                >
                  {sala.codigoSala}
                </Text>
                <CopyButton value={sala.codigoSala}>
                  {({ copied, copy }) => (
                    <Tooltip label={copied ? '¡Copiado!' : 'Copiar código'}>
                      <ActionIcon
                        size="xl"
                        color={copied ? 'green' : 'blue'}
                        onClick={copy}
                        variant="filled"
                      >
                        {copied ? <IconCheck size={24} /> : <IconCopy size={24} />}
                      </ActionIcon>
                    </Tooltip>
                  )}
                </CopyButton>
              </Group>
              <Text size="xs" color="dimmed">
                Los jugadores pueden unirse usando este código en "Unirse a Sala"
              </Text>

              {/* Botón de invitar usuarios */}
              {sala.esCreador && (
                <Button
                  variant="light"
                  color="indigo"
                  leftSection={<IconUserPlus size={18} />}
                  onClick={() => setInvitarModalOpened(true)}
                  mt="sm"
                  fullWidth
                >
                  Invitar Jugadores
                </Button>
              )}
            </Stack>
          </Card>

          <Badge 
            size="lg" 
            variant="light" 
            color={sala.estado === EstadoSala.ESPERANDO ? 'blue' : 'orange'}
          >
            {sala.estado === EstadoSala.ESPERANDO ? 'Esperando jugadores' : 'En progreso'}
          </Badge>
        </div>

        {/* Estado de conexión */}
        <Alert
          icon={wsConnected ? <IconWifi size={16} /> : <IconWifiOff size={16} />}
          color={wsConnected ? 'green' : 'orange'}
          variant="light"
        >
          {wsConnected ? 'Conectado en tiempo real' : 'Reconectando...'}
        </Alert>

        {/* Información del examen */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Card withBorder padding="lg" className="text-center">
              <ThemeIcon size={50} mx="auto" mb="md" variant="light" color="blue">
                <IconTarget size={26} />
              </ThemeIcon>
              <Text fw={500} size="lg">{sala.totalPreguntas} preguntas</Text>
              <Text size="sm" color="dimmed">Solo reactivos</Text>
            </Card>
          </Grid.Col>
          
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Card withBorder padding="lg" className="text-center">
              <ThemeIcon size={50} mx="auto" mb="md" variant="light" color="orange">
                <IconClock size={26} />
              </ThemeIcon>
              <Text fw={500} size="lg">{sala.tiempoLimiteMin} minutos</Text>
              <Text size="sm" color="dimmed">Tiempo límite</Text>
            </Card>
          </Grid.Col>
          
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Card withBorder padding="lg" className="text-center">
              <ThemeIcon size={50} mx="auto" mb="md" variant="light" color="green">
                <IconTrophy size={26} />
              </ThemeIcon>
              <Text fw={500} size="lg">Competitivo</Text>
              <Text size="sm" color="dimmed">Con ranking</Text>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Participantes */}
        <Card withBorder padding="lg">
          <Group justify="space-between" mb="md">
            <Title order={3} className="flex items-center gap-2">
              <IconUsers size={24} />
              Participantes
            </Title>
            <Badge variant="light" color="blue">
              {sala.cantidadParticipantes}/{sala.maxParticipantes}
            </Badge>
          </Group>

          <Progress 
            value={(sala.cantidadParticipantes / sala.maxParticipantes) * 100} 
            mb="lg"
            color="blue"
          />

          <Grid>
            {sala.participantes.map((participante) => (
              <Grid.Col key={participante.id} span={{ base: 12, sm: 6, md: 4 }}>
                <Card withBorder padding="md" className="relative">
                  {participante.esCreador && (
                    <Badge 
                      className="absolute -top-2 -right-2" 
                      variant="filled" 
                      color="gold"
                      size="sm"
                    >
                      Creador
                    </Badge>
                  )}
                  
                  <Group gap="md">
                    <Avatar 
                      src={participante.avatarUrl} 
                      alt={participante.nombreUsuario}
                      size="lg"
                      color="blue"
                    >
                      {participante.nombreUsuario.charAt(0).toUpperCase()}
                    </Avatar>
                    
                    <div className="flex-1">
                      <Text fw={500} size="sm">
                        {participante.nombreUsuario}
                      </Text>
                      <Badge 
                        size="xs" 
                        variant="light" 
                        color={getEstadoBadgeColor(participante.estado)}
                      >
                        {getEstadoTexto(participante.estado)}
                      </Badge>
                    </div>
                  </Group>
                </Card>
              </Grid.Col>
            ))}
            
            {/* Slots vacíos */}
            {Array.from({ length: sala.maxParticipantes - sala.cantidadParticipantes }).map((_, index) => (
              <Grid.Col key={`empty-${index}`} span={{ base: 12, sm: 6, md: 4 }}>
                <Card withBorder padding="md" className="border-dashed border-gray-300">
                  <Group gap="md">
                    <Avatar size="lg" color="gray">
                      ?
                    </Avatar>
                    <div>
                      <Text color="dimmed" size="sm">Esperando jugador...</Text>
                    </div>
                  </Group>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Card>

        {/* Controles */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            {sala.esCreador ? (
              <>
                <Alert
                  icon={<IconAlertTriangle size={16} />}
                  color="blue"
                  variant="light"
                >
                  Eres el <strong>creador</strong> de esta sala. Puedes iniciar el examen cuando todos estén listos.
                </Alert>
                
                {!puedeIniciar && (
                  <Alert color="orange" variant="light">
                    {sala.cantidadParticipantes < 2 
                      ? 'Necesitas al menos 2 participantes para iniciar'
                      : 'Esperando a que todos los participantes estén listos'
                    }
                  </Alert>
                )}
                
                <Group justify="center" gap="md">
                  <Button
                    variant="outline"
                    color="red"
                    leftSection={<IconDoorExit size={18} />}
                    onClick={handleAbandonarSala}
                  >
                    Cancelar Sala
                  </Button>
                  
                  <Button
                    size="lg"
                    variant="gradient"
                    gradient={{ from: 'green', to: 'lime' }}
                    leftSection={<IconPlayerPlay size={20} />}
                    onClick={handleIniciarExamen}
                    loading={loading}
                    disabled={!puedeIniciar || loading}
                  >
                    Iniciar Examen
                  </Button>
                </Group>
              </>
            ) : (
              <>
                <Alert
                  icon={<IconUsers size={16} />}
                  color="green"
                  variant="light"
                >
                  Esperando a que <strong>{sala.nombreCreador}</strong> inicie el examen.
                </Alert>
                
                <Group justify="center">
                  <Button
                    variant="outline"
                    color="red"
                    leftSection={<IconDoorExit size={18} />}
                    onClick={handleAbandonarSala}
                  >
                    Abandonar Sala
                  </Button>
                </Group>
              </>
            )}
          </Stack>
        </Card>
      </Stack>

      {/* Modal de invitar usuarios */}
      <InvitarUsuarioModal
        opened={invitarModalOpened}
        onClose={() => setInvitarModalOpened(false)}
        codigoSala={sala.codigoSala}
        nombreSala={sala.nombreSala}
      />
    </Container>
  );
};

export default SalaEspera;